import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Disc3, 
  Mic, 
  Wand2, 
  AlertCircle, 
  X,
  Upload,
  Download,
  Layers,
  Piano,
  BarChart3,
  ArrowRight,
  Check,
  Zap,
  Shield,
  Clock
} from 'lucide-react';

const ERROR_MESSAGES = {
  'auth_failed': 'Authentication failed. Please try again.',
  'no_code': 'Authentication was cancelled. Please try again.',
  'state_mismatch': 'Security verification failed. Please try again.',
  'missing_verifier': 'Session expired. Please try again.',
  'database_not_configured': 'Service temporarily unavailable. Please try again later.',
  'callback_failed': 'Something went wrong during sign in. Please try again.',
  'user_not_found': 'No existing account found. New sign-ups are currently disabled.',
  'signups_disabled': 'New sign-ups are currently disabled. Existing users can still log in.',
  'already_exists': 'An account with this email already exists. Please log in instead.',
};

export default function Home() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(ERROR_MESSAGES[errorParam] || 'An error occurred. Please try again.');
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('error');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  const dismissError = () => {
    setError(null);
  };

  const handleLogin = () => {
    window.location.href = '/api/auth/login';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#0b0d10] via-[#0e1216] to-[#0b0d10]">
        <div className="animate-pulse">
          <Disc3 className="h-12 w-12 text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0b0d10] via-[#0e1216] to-[#0b0d10]">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/20 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Disc3 className="h-4 w-4 text-primary" />
              </div>
              <span className="text-lg font-semibold text-white">RecordRinse.com</span>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={handleLogin}
                className="text-gray-300 hover:text-white"
              >
                Log In
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {error && (
        <div className="fixed top-20 left-0 right-0 z-50 px-4">
          <div className="max-w-md mx-auto">
            <Alert variant="destructive" className="bg-red-500/10 border-red-500/30 shadow-lg">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between gap-4">
                <span>{error}</span>
                <button 
                  onClick={dismissError}
                  className="text-red-400 hover:text-red-300 transition-colors flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </button>
              </AlertDescription>
            </Alert>
          </div>
        </div>
      )}

      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 text-green-400 text-sm font-medium mb-8">
              <Disc3 className="h-4 w-4" />
              New sign-ups are currently disabled
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Clean Explicit Content
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-400">
                From Any Song Instantly
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-400 mb-10 max-w-3xl mx-auto">
              Remove curse words and explicit language from your favorite songs with professional-grade AI. 
              Perfect for radio edits, family-friendly playlists, and content creators.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <Button 
                size="lg" 
                onClick={handleLogin}
                className="w-full sm:w-auto px-8 py-6 text-lg bg-primary hover:bg-primary/90 group"
              >
                Log In to Clean Audio
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                onClick={handleLogin}
                className="w-full sm:w-auto px-8 py-6 text-lg border-gray-700 text-gray-300 hover:bg-white/5"
              >
                I Have an Account
              </Button>
            </div>

            <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <Check className="h-4 w-4 text-green-500" />
                Existing accounts only
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="h-4 w-4 text-green-500" />
                Log in to continue
              </span>
            </div>
          </div>
        </section>

        {/* 3-Step Process Section */}
        <section className="py-20 px-4 bg-gradient-to-b from-transparent via-primary/5 to-transparent">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Clean Your Audio in 3 Simple Steps
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Our AI-powered system automatically detects and removes explicit content, 
                giving you a clean version in minutes.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <StepCard
                number="1"
                icon={<Upload className="h-8 w-8" />}
                title="Upload Your Song"
                description="Drag and drop any audio file (MP3, WAV, M4A, and more). We support files up to 2GB with no quality loss."
                features={['All major formats supported', 'Preserves original quality', 'Secure cloud upload']}
              />
              <StepCard
                number="2"
                icon={<Wand2 className="h-8 w-8" />}
                title="AI Processing"
                description="Our AI separates vocals, transcribes lyrics, identifies explicit content, and applies precise filtering."
                features={['Automatic curse detection', 'Smart audio filtering', 'Natural-sounding results']}
              />
              <StepCard
                number="3"
                icon={<Download className="h-8 w-8" />}
                title="Download Clean Audio"
                description="Get your clean version with explicit content removed. Download in your preferred format with metadata intact."
                features={['Multiple export formats', 'Original metadata preserved', 'Instant download']}
              />
            </div>
          </div>
        </section>

        {/* Features Grid Section */}
        <section className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 text-purple-400 text-sm font-medium mb-4">
                <Zap className="h-4 w-4" />
                Professional Tools
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Everything You Need for Audio Production
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Beyond cleaning audio, RecordRinse.com offers a complete suite of AI-powered tools 
                for music producers, DJs, and content creators.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FeatureCard
                icon={<Layers className="h-6 w-6" />}
                title="Stem Separation"
                description="Isolate vocals, drums, bass, piano, guitars, synths, and more. Extract individual instruments from any track with studio-quality AI separation."
                gradient="from-blue-500/20 to-cyan-500/20"
                iconColor="text-blue-400"
              />
              <FeatureCard
                icon={<Piano className="h-6 w-6" />}
                title="Audio to MIDI"
                description="Convert any audio stem to MIDI format. Perfect for remixing, sampling, or recreating songs in your DAW with full editability."
                gradient="from-purple-500/20 to-pink-500/20"
                iconColor="text-purple-400"
              />
              <FeatureCard
                icon={<BarChart3 className="h-6 w-6" />}
                title="Track Analytics"
                description="Get detailed insights: BPM, key, chord progressions, song structure, genre, mood, spectral analysis, and instrument detection."
                gradient="from-yellow-500/20 to-amber-500/20"
                iconColor="text-yellow-400"
              />
              <FeatureCard
                icon={<Mic className="h-6 w-6" />}
                title="AI Transcription"
                description="Get accurate word-by-word transcriptions with timestamps. Powered by OpenAI Whisper for industry-leading accuracy."
                gradient="from-violet-500/20 to-purple-500/20"
                iconColor="text-violet-400"
              />
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-20 px-4 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <TrustCard
                icon={<Zap className="h-8 w-8" />}
                title="Lightning Fast"
                description="Process songs in minutes, not hours. Our cloud infrastructure handles the heavy lifting."
              />
              <TrustCard
                icon={<Shield className="h-8 w-8" />}
                title="Secure & Private"
                description="Your files are encrypted and automatically deleted after processing. We never share your data."
              />
              <TrustCard
                icon={<Clock className="h-8 w-8" />}
                title="Available 24/7"
                description="Access your audio tools anytime, anywhere. No software to install, works in any browser."
              />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-3xl bg-gradient-to-r from-primary/20 via-purple-500/20 to-primary/20 p-px">
              <div className="rounded-3xl bg-[#0e1216] p-12 text-center">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Ready to Clean Your Next Song?
                </h2>
                <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
                  Existing users can log in to continue processing audio.
                  New sign-ups are currently disabled.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button 
                    size="lg" 
                    variant="ghost" 
                    onClick={handleLogin}
                    className="w-full sm:w-auto px-8 py-6 text-lg text-gray-300 hover:text-white hover:bg-white/5"
                  >
                    Sign In
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-500 text-sm">
          &copy; {new Date().getFullYear()} RecordRinse.com. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

function StepCard({ number, icon, title, description, features }) {
  return (
    <div className="relative">
      <div className="absolute -top-4 left-6 z-10">
        <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-primary/30">
          {number}
        </div>
      </div>
      <div className="pt-8 p-6 rounded-2xl bg-white/5 border border-white/10 h-full hover:bg-white/[0.07] transition-colors">
        <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-5">
          {icon}
        </div>
        <h3 className="text-xl font-semibold text-white mb-3">{title}</h3>
        <p className="text-gray-400 text-sm mb-4">{description}</p>
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center gap-2 text-sm text-gray-500">
              <Check className="h-4 w-4 text-primary flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description, gradient, iconColor }) {
  return (
    <div className={`p-6 rounded-2xl bg-gradient-to-br ${gradient} border border-white/10 hover:border-white/20 transition-all hover:scale-[1.02]`}>
      <div className={`h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center ${iconColor} mb-4`}>
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
    </div>
  );
}

function TrustCard({ icon, title, description }) {
  return (
    <div className="p-6">
      <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mx-auto mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
      <p className="text-gray-400 text-sm">{description}</p>
    </div>
  );
}
